/**
 * 全局返回键处理服务
 * 统一管理所有页面的返回键行为
 */
import backButtonHandler from '@/utils/BackButtonHandler.js';

class GlobalBackButtonService {
  constructor() {
    this.backButtonHandler = backButtonHandler;
    this.isInitialized = false;
    this.currentPageType = null;
    this.pageHandlers = new Map(); // 存储不同页面的处理逻辑
  }

  /**
   * 初始化全局返回键处理
   */
  init() {
    if (this.isInitialized) {
      console.log('🔙 [全局返回键] 已经初始化，跳过重复初始化');
      return;
    }

    console.log('🔙 [全局返回键] 开始初始化全局返回键处理服务...');
    
    // 初始化 BackButtonHandler
    this.backButtonHandler.init({
      onModalClose: () => this.handleModalClose(),
      onPageBack: () => this.handlePageBack(),
      onExitConfirm: () => this.handleExitConfirm(),
      onStateChange: (state, historyStack) => this.handleStateChange(state, historyStack)
    });

    this.isInitialized = true;
    console.log('✅ [全局返回键] 全局返回键处理服务初始化完成');
  }

  /**
   * 注册页面处理逻辑
   * @param {string} pageType 页面类型
   * @param {Object} handlers 处理逻辑对象
   */
  registerPageHandler(pageType, handlers) {
    console.log('🔙 [全局返回键] 注册页面处理逻辑:', pageType);
    console.log('🔙 [全局返回键] 注册的处理器:', handlers);
    this.pageHandlers.set(pageType, handlers);
    console.log('🔙 [全局返回键] 当前所有注册的处理器:', Array.from(this.pageHandlers.keys()));
  }

  /**
   * 取消注册页面处理逻辑
   * @param {string} pageType 页面类型
   */
  unregisterPageHandler(pageType) {
    console.log('🔙 [全局返回键] 取消注册页面处理逻辑:', pageType);
    this.pageHandlers.delete(pageType);
    console.log('🔙 [全局返回键] 取消注册后剩余的处理器:', Array.from(this.pageHandlers.keys()));
  }

  /**
   * 设置当前页面类型
   * @param {string} pageType 页面类型
   */
  setCurrentPageType(pageType) {
    console.log('🔙 [全局返回键] 设置当前页面类型:', pageType);
    this.currentPageType = pageType;
  }

  /**
   * 处理弹窗关闭
   */
  handleModalClose() {
    console.log('🔙 [全局返回键] 处理弹窗关闭');
    console.log('🔙 [全局返回键] 当前注册的页面处理器:', Array.from(this.pageHandlers.keys()));
    
    // 🔥 新增：优先检查视频全屏模态框
    if (this.pageHandlers.has('video-fullscreen')) {
      console.log('🔙 [全局返回键] 检测到视频全屏模态框，优先处理');
      const handlers = this.pageHandlers.get('video-fullscreen');
      if (handlers.onModalClose) {
        console.log('🔙 [全局返回键] 执行视频全屏关闭回调');
        handlers.onModalClose();
        // 🔥 修复：处理完成后立即清理注册，避免重复处理
        this.unregisterPageHandler('video-fullscreen');
        
        // 🔥 关键修复：视频全屏关闭后，确保播放页面处理器仍然存在
        console.log('🔙 [全局返回键] 视频全屏关闭后，检查播放页面处理器状态');
        if (!this.pageHandlers.has('lyric-player')) {
          console.log('🔙 [全局返回键] 播放页面处理器不存在，需要重新注册');
          // 这里需要通知播放页面重新注册处理器
          // 通过事件通知的方式，让播放页面自己重新注册
          window.dispatchEvent(new CustomEvent('video-fullscreen-closed', {
            detail: { needReregister: true }
          }));
        } else {
          console.log('🔙 [全局返回键] 播放页面处理器仍然存在');
        }
        
        return; // 处理完成后直接返回，不继续处理其他弹窗
      }
    } else {
      console.log('🔙 [全局返回键] 未检测到视频全屏模态框处理器');
    }
    
    // 🎵 新增：检查播放页面（LyricPlayer）
    if (this.pageHandlers.has('lyric-player')) {
      console.log('🔙 [全局返回键] 检测到播放页面，优先处理');
      const handlers = this.pageHandlers.get('lyric-player');
      if (handlers.onModalClose) {
        console.log('🔙 [全局返回键] 执行播放页面关闭回调');
        handlers.onModalClose();
        // 🔥 修复：处理完成后立即清理注册，避免重复处理
        this.unregisterPageHandler('lyric-player');
        return; // 处理完成后直接返回，不继续处理其他弹窗
      }
    } else {
      console.log('🔙 [全局返回键] 未检测到播放页面处理器');
    }
    
    if (this.currentPageType && this.pageHandlers.has(this.currentPageType)) {
      const handlers = this.pageHandlers.get(this.currentPageType);
      if (handlers.onModalClose) {
        handlers.onModalClose();
      }
    }
  }

  /**
   * 处理页面返回
   */
  handlePageBack() {
    console.log('🔙 [全局返回键] 处理页面返回');
    console.log('🔙 [全局返回键] 当前注册的页面处理器:', Array.from(this.pageHandlers.keys()));
    
    // 🎵 新增：检查播放页面（LyricPlayer）
    if (this.pageHandlers.has('lyric-player')) {
      console.log('🔙 [全局返回键] 检测到播放页面，优先处理页面返回');
      const handlers = this.pageHandlers.get('lyric-player');
      if (handlers.onPageBack) {
        console.log('🔙 [全局返回键] 执行播放页面返回回调');
        handlers.onPageBack();
        // 🔥 修复：处理完成后立即清理注册，避免重复处理
        this.unregisterPageHandler('lyric-player');
        return; // 处理完成后直接返回，不继续处理其他页面
      }
    } else {
      console.log('🔙 [全局返回键] 未检测到播放页面处理器');
    }
    
    if (this.currentPageType && this.pageHandlers.has(this.currentPageType)) {
      const handlers = this.pageHandlers.get(this.currentPageType);
      if (handlers.onPageBack) {
        handlers.onPageBack();
      }
    } else {
      // 默认行为：显示退出确认
      console.log('🔙 [全局返回键] 没有找到页面处理逻辑，显示退出确认');
      this.handleExitConfirm();
    }
  }

  /**
   * 处理退出确认
   */
  handleExitConfirm() {
    console.log('🔙 [全局返回键] 处理退出确认');
    
    if (this.currentPageType && this.pageHandlers.has(this.currentPageType)) {
      const handlers = this.pageHandlers.get(this.currentPageType);
      if (handlers.onExitConfirm) {
        handlers.onExitConfirm();
      }
    } else {
      // 默认行为：显示浏览器默认的退出确认
      console.log('🔙 [全局返回键] 没有找到页面处理逻辑，使用默认退出确认');
      this.showDefaultExitConfirm();
    }
  }

  /**
   * 处理状态变化
   * @param {Object} state 状态对象
   * @param {Array} historyStack 历史栈
   */
  handleStateChange(state, historyStack) {
    console.log('🔙 [全局返回键] 状态变化:', state.state, '历史栈长度:', historyStack.length);
    
    if (this.currentPageType && this.pageHandlers.has(this.currentPageType)) {
      const handlers = this.pageHandlers.get(this.currentPageType);
      if (handlers.onStateChange) {
        handlers.onStateChange(state, historyStack);
      }
    }
  }

  /**
   * 显示默认退出确认
   */
  showDefaultExitConfirm() {
    // 使用浏览器默认的退出确认
    if (confirm('确定要离开这个页面吗？')) {
      window.close();
    }
  }

  /**
   * 推入状态到历史栈
   * @param {string} state 状态标识
   * @param {string} title 状态标题
   * @param {Object} data 附加数据
   */
  pushState(state, title = '', data = {}) {
    this.backButtonHandler.pushState(state, title, data);
  }

  /**
   * 获取历史栈
   * @returns {Array}
   */
  getHistoryStack() {
    return this.backButtonHandler.getHistoryStack();
  }

  /**
   * 获取当前状态
   * @returns {Object}
   */
  getCurrentState() {
    return this.backButtonHandler.getCurrentState();
  }

  /**
   * 清空历史栈
   */
  clearHistory() {
    this.backButtonHandler.clearHistory();
  }

  /**
   * 销毁服务
   */
  destroy() {
    console.log('🔙 [全局返回键] 销毁全局返回键处理服务');
    this.backButtonHandler.destroy();
    this.pageHandlers.clear();
    this.currentPageType = null;
    this.isInitialized = false;
  }
}

// 创建单例实例
const globalBackButtonService = new GlobalBackButtonService();

export default globalBackButtonService;
